import { VisibilityOutlined } from '@mui/icons-material';
import { ApiResponse } from '../../services/apiService';
import { React, useState, useEffect } from '../../utils/globalImports';
import { AppLayout, FilterCard, CustomDataTable, useApi, Toaster, validationMessages, CustomBackdrop, useNavigate, dayjs } from '../../utils/globalImports';

interface Project {
    projectId?: number;
    projectNumber?: string;
    projectName: string;
    clientId?: number;
    clientName?: string;
    startDate?: Date;
    endDate?: Date;
    description?: string;
    proposalTagId?: number;
    assistantProjectManagerId?: number;
    projectManagerId?: number;
    bidCoordinatorId?: number;
    technicalProjectLeadId?: number;
    assistantTechnicalProjectLeadId?: number;
    status: number;
    createdId?: number;
    updatedId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

interface Client {
    clientId: number;
    clientName: string;
    status: number;
}

const Projects: React.FC = () => {
    const navigate = useNavigate();
    const [projectDatalist, setProjectDatalist] = useState<any[]>([]);
    const [projectData, setProjectData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isOn, setIsOn] = useState<boolean>();
    const [selectedProject, setSelectedProject] = useState<any | null>(null);
    const [currentStatus, setCurrentStatus] = useState<string[]>(['Active']);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const { execute, loading } = useApi();
    const [noResultsMessage, setNoResultsMessage] = useState<string>("");
    const [clientList, setClientList] = useState<Client[]>([]);

    const props = { title: 'Projects', actionText: 'Add Project', searchbarText: validationMessages.project_searchText, showframeworkField: true, };
    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        fetchProjects();
    }, []);

    const sortOptions = [
        { value: "Project Name", text: "Project Name" },
        { value: "Project Number", text: "Project Number" },
        { value: "Associated Clients", text: "Associated Clients" }
    ];

    const fetchProjects = async () => {
        const { data: projectData } = await execute<Project[]>('get', '/Project/GetAllProjects');
        // const { data: clientData } = await execute<Client[]>('get', '/Client/GetAllClients');

        if (projectData) {
            setProjectDatalist(projectData);
            // setClientList(clientData);

            // // Properly typed clientMap
            // const clientMap: Record<number, string> = clientData.reduce((map, client) => {
            //     map[client.clientId] = client.clientName;
            //     return map;
            // }, {} as Record<number, string>);

            const formattedProjectData = projectData.map((project) => {
                const { projectId, clientId, clientName, projectNumber, projectName, status } = project;
                // const clientName = clientMap[clientId ?? 0] || 'Unknown Client'; 
                const Status = status === 1 ? 'Active' : 'Inactive';

                return {
                    projectId,
                    clientId,
                    projectNumber,
                    projectName,
                    clientName,
                    Status
                };
            });

            setProjectData(formattedProjectData);
            const activeProjects = formattedProjectData.filter((project) =>
                currentStatus.includes(project.Status)
            );
            showGrid(activeProjects);
        }
    };

    const showGrid = (dataList: any[]) => {
        const projects = dataList
            .map((item) => {
                const { projectId, clientId, projectNumber, projectName, clientName, Status } = item;
                return {
                    projectId,
                    clientId,
                    "Project Number": projectNumber,
                    "Project Name": projectName,
                    "Associated Clients": clientName,
                    Status
                };
            })
            .sort((a, b) =>
                a["Project Name"].localeCompare(b["Project Name"], undefined, { sensitivity: 'base' }) ||
                a["Associated Clients"].localeCompare(b["Associated Clients"], undefined, { sensitivity: 'base' })
            );

        setFilteredData(projects);
    };

    const handleSearchGridData = (
        query: string,
        status: number,
        category?: number,
        region?: number,
        contactType?: number,
        date?: string
    ) => {
        const lowerCaseQuery = query.toLowerCase();

        const statusMapping: Record<number, string> = {
            1: "Active",
            0: "Inactive",
        };

        setCurrentStatus(
            status === -1
                ? ["Active", "Inactive"]
                : status === 1
                    ? ["Active"]
                    : ["Inactive"]
        );


        // ✅ Use `clientList` instead of undefined `clientData`
        // const clientMap: Record<number, string> = clientList.reduce((map, client) => {
        //     map[client.clientId] = client.clientName;
        //     return map;
        // }, {} as Record<number, string>);

        const filtered = projectDatalist
            .filter((project: Project) => {
                const projectStatus = project.status === 1 ? "Active" : "Inactive";

                const matchesQuery =
                    (project.projectNumber ?? "").toLowerCase().includes(lowerCaseQuery) ||
                    (project.projectName ?? "").toLowerCase().includes(lowerCaseQuery) ||
                    (project.clientName ?? "").toLowerCase().includes(lowerCaseQuery);

                const matchesStatus =
                    status === -1 ||
                    projectStatus === statusMapping[status];

                const matchesDate =
                    date && project.endDate
                        ? dayjs(project.endDate).isSame(dayjs(date), "day")
                        : true;

                return matchesQuery && matchesStatus && matchesDate;
            })
            .map((project) => ({
                projectId: project.projectId,
                clientId: project.clientId,
                projectNumber: project.projectNumber,
                projectName: project.projectName,
                clientName: project.clientName,
                Status: project.status === 1 ? "Active" : "Inactive",
            }));

        // console.log("Filtered", filtered);
        setFilteredData(filtered);
        showGrid(filtered);
    };

    const handleEdit = (project: any) => {
        handleNavigate(project.projectId);
    };

    const handleNavigate = (projectId?: number) => {
        if (projectId) navigate(`/project/edit-projects/${projectId}/general`)
        else navigate('/project/add-projects');
    };

    // const handleColumnLinkClick = (row: any, columnName: string) => {
    //     if (columnName === 'Project Name') {
    //         navigate(`/project/edit-projects/${row.projectId}`);
    //     }
    // };

    const handleToggleState = (row: any) => {
        setIsOn(row.Status === 'Active');
        setSelectedProject(row);
        setDialogOpen(true);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedProject(null);
    };

    const handleDialogConfirm = async () => {
        if (!selectedProject) return;
        try {
            const updatedProject = projectDatalist.find((project) => project.projectId === selectedProject.projectId);
            if (updatedProject) {
                const updatedProjectData = { ...updatedProject, status: selectedProject.Status === 'Active' ? 0 : 1, updatedId: userId };
                let response: ApiResponse<any> = await execute('put', '/Project/UpdateProject', updatedProjectData);
                if (response) {
                    if (response.error) {
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
            }
            fetchProjects();
            setSelectedProject(null);
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };

    return (
        <AppLayout>
            <FilterCard
                {...props}
                handleModalOrNavigate={() => handleNavigate()}
                handleSearch={handleSearchGridData}
            />
            {projectDatalist.length > 0 && (
                <CustomDataTable
                    rows={filteredData}
                    actionText="Project"
                    sortOptions={sortOptions}
                    onRowEdit={handleEdit}
                    onRowDoubleClick={handleEdit}
                    onToggleClick={handleToggleState}
                    //clickableColumns={['Project Name']}
                    //onColumnLinkClick={handleColumnLinkClick}
                    isDialogOpen={dialogOpen}
                    isToggleOn={isOn}
                    handleCancel={handleDialogCancel}
                    handleConfirm={handleDialogConfirm}
                    noResultMessage={validationMessages.common_RecordNotFound("projects")}
                    customActionIcon={<VisibilityOutlined sx={{ color:'#1e66ab' }} />}
                    customActionTooltip="Proposal List"
                    onCustomActionClick={(row) =>
                        navigate(
            `/proposal/proposals/${row.projectId}/${encodeURIComponent(row["Project Name"])}`
        )
                    }
                />
            )}
            <Toaster
                open={toast.open}
                message={toast.message}
                type={toast.type as "success" | "error"}
                onClose={() => setToast({ ...toast, open: false })}
            />
            <CustomBackdrop open={loading} />
        </AppLayout>
    );
};

export default Projects;